/**
 * Two-way fillable AQ PDF client (#13).
 *
 * - downloadAqFillable: GET the blank branded fillable PDF (same-origin,
 *   credentials) → Blob + server-named file.
 * - importAqPdf: POST a filled PDF (multipart) → parsed questionnaire payload
 *   { answers, financials, warnings } so the caller can populate a draft for
 *   review. The server records an append-only aq_imports audit row.
 */
import { downloadBlob } from './iaaExports';
import type { UUID } from './types';

export { downloadBlob };

export interface AqImportResult {
  import_id: string;
  answers: Record<string, unknown>;
  financials: Record<string, unknown>;
  warnings: string[];
}

async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON */ }
  return typeof detail === 'string' ? detail : fallback;
}

export async function importAqPdf(agreementId: UUID, file: File): Promise<AqImportResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/agreements/${agreementId}/aq-imports`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    throw new Error(await detailOf(res, 'The questionnaire PDF could not be read.'));
  }
  return res.json();
}

export async function downloadAqFillable(
  agreementId: UUID,
): Promise<{ blob: Blob; fileName: string }> {
  const res = await fetch(`/agreements/${agreementId}/aq-fillable`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(await detailOf(res, 'The fillable PDF could not be generated.'));
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const fileName = m ? decodeURIComponent(m[1]) : 'AnexoII_ADQC_fillable.pdf';
  return { blob: await res.blob(), fileName };
}
